import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const LoginScreen: React.FC = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const { login, register, isRegistrationOpen, appAssets, verifyTwoFactorCode } = useAuth();
  
  // Login flow state
  const [loginStep, setLoginStep] = useState<'credentials' | '2fa'>('credentials');
  const [userIdFor2fa, setUserIdFor2fa] = useState<string | null>(null);

  // Common state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Registration specific state
  const [email, setEmail] = useState('');
  const [authCode, setAuthCode] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    const result = await login(username, password);
    if (!result.success) {
      setError(result.message);
      setIsLoading(false);
    } else if (result.twoFactorRequired && result.userId) {
        setUserIdFor2fa(result.userId);
        setLoginStep('2fa');
        setIsLoading(false);
    }
  };

  const handleVerify2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userIdFor2fa || !twoFactorCode) return;
    setIsLoading(true);
    setError('');
    const result = await verifyTwoFactorCode(userIdFor2fa, twoFactorCode);
    if (!result.success) {
        setError(result.message);
        setIsLoading(false);
    }
    // On success, App component will handle navigation
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if(password.length < 6) {
        setError('Password must be at least 6 characters long.');
        return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    const result = await register(username, password, email, authCode);
    if (result.success) {
        setSuccess(result.message);
        setIsRegistering(false); // Switch back to login view
        // Clear fields
        setUsername('');
        setPassword('');
        setEmail('');
        setAuthCode('');
    } else {
        setError(result.message);
    }
    setIsLoading(false);
  };

  const toggleView = () => {
      setIsRegistering(!isRegistering);
      setError('');
      setSuccess('');
      setUsername('');
      setPassword('');
      setLoginStep('credentials');
  }

  const renderCredentialsForm = () => (
     <form className="mt-8 space-y-6" onSubmit={handleLogin}>
        <div className="space-y-4">
            <input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
        </div>
        {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
        {success && <p className="text-green-400 text-sm text-center pt-2">{success}</p>}
        <div>
            <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-slate-900 disabled:bg-slate-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
        </div>
    </form>
  );

  const render2faForm = () => (
     <form className="mt-8 space-y-6" onSubmit={handleVerify2fa}>
        <div className="text-center">
            <h3 className="mt-4 text-xl font-bold text-slate-100">Two-Factor Authentication</h3>
            <p className="mt-1 text-sm text-slate-400">Enter the code from your authenticator app.</p>
        </div>
        <div className="space-y-4">
            <input 
                id="2fa-code" 
                value={twoFactorCode} 
                onChange={(e) => setTwoFactorCode(e.target.value)} 
                placeholder="6-digit code" 
                required 
                className="appearance-none text-center tracking-[0.5em] font-bold text-lg relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" 
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
            />
        </div>
        {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
        <div>
            <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 focus:ring-offset-slate-900 disabled:bg-slate-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                {isLoading ? 'Verifying...' : 'Verify'}
            </button>
        </div>
         <div className="text-center">
             <button type="button" onClick={() => setLoginStep('credentials')} className="text-sm font-medium text-slate-400 hover:text-slate-200">
                Back to login
            </button>
        </div>
    </form>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-900/60 backdrop-blur-xl rounded-2xl shadow-2xl p-8 space-y-8 border border-blue-500/20 shadow-[0_0_25px_rgba(59,130,246,0.1)]">
        {loginStep === 'credentials' && (
            <div className="text-center">
                <div className="flex justify-center items-center gap-4 mb-6 h-16">
                  {appAssets.logo1 && <img src={appAssets.logo1} alt="Primary Logo" className="max-h-full max-w-[45%] object-contain" />}
                  {appAssets.logo2 && <img src={appAssets.logo2} alt="Secondary Logo" className="max-h-full max-w-[45%] object-contain" />}
                </div>
                {appAssets.animatedLogo ? (
                  <img src={appAssets.animatedLogo} alt="App Logo" className="mx-auto h-24 w-auto" />
                ) : null}
                <h2 className="mt-6 text-3xl font-extrabold text-slate-100">
                    JT-Crew Movement
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                    {isRegistering ? 'Create a new account' : 'Sign in to your account'}
                </p>
            </div>
        )}
        
        {isRegistering ? (
             <form className="mt-8 space-y-6" onSubmit={handleRegister}>
                <div className="space-y-4">
                    <input value={username} onChange={(e) => setUsername(e.target.value)} id="username-reg" placeholder="Username" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} id="email-reg" placeholder="Email" type="email" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                    <input value={password} onChange={(e) => setPassword(e.target.value)} id="password-reg" placeholder="Password" type="password" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                    <input value={authCode} onChange={(e) => setAuthCode(e.target.value)} id="authcode-reg" placeholder="Authorization Code" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                </div>
                {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
                <div>
                    <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-slate-900 disabled:bg-slate-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                        {isLoading ? 'Registering...' : 'Register'}
                    </button>
                </div>
             </form>
        ) : loginStep === 'credentials' ? (
            renderCredentialsForm()
        ) : (
            render2faForm()
        )}

        {loginStep === 'credentials' && (
             <div className="text-sm text-center space-y-4 pt-4 border-t border-slate-700/50">
                <button onClick={toggleView} className="font-medium text-blue-400 hover:text-blue-300" disabled={!isRegistrationOpen && !isRegistering}>
                    {isRegistering ? 'Already have an account? Sign in' : (isRegistrationOpen ? "Don't have an account? Register" : "")}
                </button>
                <p className="text-xs text-slate-500">
                        Crew-movement Developed By John tawfik One Of <span className="font-semibold text-amber-400">EAS</span> Team Members
                    </p>
                </div>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;